import React, { useMemo } from "react";
import { STADIUM_VIEWBOX, ZONE_PATHS, pathCentroid } from "@/data/stadiumPaths";

const heatColor = (pct) => {
  if (pct >= 0.95) return "hsl(var(--destructive))";
  if (pct >= 0.8)  return "hsl(var(--warning))";
  if (pct >= 0.6)  return "hsl(var(--primary))";
  if (pct >= 0.4)  return "hsl(217 91% 60%)";
  return "hsl(var(--muted-foreground))";
};

/**
 * Stadium SVG with PREDICTED density (not current) for selected horizon.
 * forecasts: [{ zone_id, capacity, forecasts: [{ horizon_min, pct, value, risk }] }]
 */
export const ForecastHeatmap = ({ forecasts = [], horizonMin = 30, onZoneClick }) => {
  const byZone = useMemo(() => {
    const m = {};
    for (const z of forecasts) {
      const f = z.forecasts?.find((x) => x.horizon_min === horizonMin);
      if (f) m[z.zone_id] = { ...z, ...f };
    }
    return m;
  }, [forecasts, horizonMin]);

  return (
    <div className="w-full h-full relative">
      <svg viewBox={STADIUM_VIEWBOX} className="w-full h-full" preserveAspectRatio="xMidYMid meet" data-testid="forecast-heatmap">
        {/* Pitch */}
        <ellipse cx="500" cy="360" rx="220" ry="160" fill="hsl(var(--surface-elevated))" stroke="hsl(var(--border))" strokeWidth="1.5" />
        <rect x="486" y="320" width="28" height="80" fill="hsl(var(--muted))" stroke="hsl(var(--border))" />
        <text x="500" y="365" textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))" fontFamily="IBM Plex Mono, monospace" letterSpacing="3">Pitch</text>

        {Object.entries(ZONE_PATHS).map(([zid, d]) => {
          const f = byZone[zid];
          const pct = f?.pct ?? 0;
          const fill = heatColor(pct);
          const c = pathCentroid(d);
          return (
            <g key={zid} className="cursor-pointer" onClick={() => onZoneClick?.(zid)} data-testid={`fc-zone-${zid}`}>
              <path d={d} fill={fill} fillOpacity={0.18 + pct * 0.6} stroke={fill} strokeOpacity={0.75} strokeWidth={1} />
              {pct >= 0.9 && <path d={d} fill="none" stroke="hsl(var(--destructive))" strokeWidth="1.5" className="flow-path" />}
              {f && (
                <>
                  <text x={c.x} y={c.y - 4} textAnchor="middle" fontSize="9" fill="hsl(var(--foreground))" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">
                    {f.zone_name}
                  </text>
                  <text x={c.x} y={c.y + 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="hsl(var(--foreground))" fontFamily="IBM Plex Mono, monospace">
                    {Math.round(pct * 100)}%
                  </text>
                </>
              )}
            </g>
          );
        })}
      </svg>

      <div className="absolute top-2 left-2 panel-elevated px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
        forecast · T+{horizonMin}m
      </div>
      <div className="absolute bottom-2 left-2 panel-elevated px-2 py-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
        <span>pred density</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--muted-foreground))" }} /> &lt;40</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(217 91% 60%)" }} /> 40+</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--primary))" }} /> 60+</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--warning))" }} /> 80+</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--destructive))" }} /> 95+</span>
      </div>
    </div>
  );
};

export default ForecastHeatmap;
